import React, { useCallback, useEffect, useState } from "react";
import { LayoutWrapper } from "../../../components/layoutWrapper";
import {
  Button,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from "@material-ui/core";
import { Alert } from "@material-ui/lab";
import useStyles from "../../../components/util/styles";
import Colors from "../../../components/util/colors";
import PdfUpload, { UploadedPdf } from "../../../components/pdfUpload";
import {
  createMarketReport,
  deleteMarketReport,
  getAllMarketReports,
  updateMarketReport,
} from "../../../lib/marketReportRequests";
import { MarketReport } from "../../../types/MarketReport";
import { marketReportTableHeadCells, rowsPerPageOptions } from "../../../constants";
import { formatDate } from "../../../utils/admin";
import { useAlert } from "../../../context/alertContext";

type ReportForm = {
  title: string;
  publishedMonth: string;
  isPublished: boolean;
  fileUrl: string;
  fileKey: string | null;
};

const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

// The API stores the first of the covered month; the form edits it as YYYY-MM.
const toMonthInput = (publishedAt: string) => publishedAt.slice(0, 7);
const toPublishedAt = (publishedMonth: string) => `${publishedMonth}-01`;

const monthLabel = (publishedMonth: string) => {
  const [year, month] = publishedMonth.split("-").map(Number);
  if (!year || !month || month < 1 || month > 12) return "";
  return `${MONTH_NAMES[month - 1]} ${year}`;
};

const emptyForm: ReportForm = {
  title: "",
  publishedMonth: "",
  isPublished: true,
  fileUrl: "",
  fileKey: null,
};

const errorMessage = (err: any, fallback: string) =>
  // post() rejects with the API's `err` string, not an Error
  typeof err === "string" ? err : err?.message || fallback;

export const AdminMarketReports = () => {
  const classes = useStyles();
  const alert = useAlert();

  const [reports, setReports] = useState<MarketReport[]>([]);
  const [loading, setLoading] = useState(true);
  const [initialError, setInitialError] = useState("");

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const [editing, setEditing] = useState<MarketReport | null>(null);
  const [isFormOpen, setFormOpen] = useState(false);
  const [form, setForm] = useState<ReportForm>(emptyForm);
  const [formError, setFormError] = useState("");
  const [saving, setSaving] = useState(false);

  const [pendingDelete, setPendingDelete] = useState<MarketReport | null>(null);
  const [deleting, setDeleting] = useState(false);

  const loadReports = useCallback(async () => {
    try {
      const response = await getAllMarketReports();
      setReports(response.marketReports);
    } catch (err: any) {
      setInitialError(
        errorMessage(err, "Market reports could not be loaded.")
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadReports();
  }, [loadReports]);

  const openCreateForm = () => {
    setEditing(null);
    setForm(emptyForm);
    setFormError("");
    setFormOpen(true);
  };

  const openEditForm = (report: MarketReport) => {
    setEditing(report);
    setForm({
      title: report.title,
      publishedMonth: toMonthInput(report.published_at),
      isPublished: report.is_published,
      fileUrl: report.file_url,
      fileKey: report.file_key,
    });
    setFormError("");
    setFormOpen(true);
  };

  const closeForm = () => {
    setFormOpen(false);
    setEditing(null);
    setForm(emptyForm);
    setFormError("");
  };

  const handleMonthChange = (publishedMonth: string) => {
    // Reports are named after the month they cover, so offer that as the title
    // until an admin types something of their own.
    const suggestion = monthLabel(publishedMonth);
    const titleIsSuggested =
      form.title === "" || form.title === monthLabel(form.publishedMonth);

    setForm({
      ...form,
      publishedMonth,
      title: titleIsSuggested && suggestion ? suggestion : form.title,
    });
  };

  const handleUploaded = (pdf: UploadedPdf) => {
    setFormError("");
    setForm((current) => ({
      ...current,
      fileUrl: pdf.fileUrl,
      fileKey: pdf.fileKey,
    }));
  };

  const handleSave = async () => {
    const title = form.title.trim();

    if (!title) {
      setFormError("A title is required.");
      return;
    }
    if (!monthLabel(form.publishedMonth)) {
      setFormError("A report month is required.");
      return;
    }
    if (!form.fileUrl) {
      setFormError("Upload the report PDF before saving.");
      return;
    }

    const params = {
      title,
      fileUrl: form.fileUrl,
      fileKey: form.fileKey,
      publishedAt: toPublishedAt(form.publishedMonth),
      isPublished: form.isPublished,
    };

    setSaving(true);
    try {
      const response = editing
        ? await updateMarketReport(editing.id, params)
        : await createMarketReport(params);

      alert.openAlert(
        response.data?.msg || "The market report has been saved.",
        "success",
        6000
      );
      closeForm();
      await loadReports();
    } catch (err: any) {
      setFormError(errorMessage(err, "The market report could not be saved."));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!pendingDelete) return;

    setDeleting(true);
    try {
      const response = await deleteMarketReport(pendingDelete.id);
      alert.openAlert(
        response.data?.msg || "The market report has been removed.",
        "success",
        6000
      );
      setPendingDelete(null);
      await loadReports();
    } catch (err: any) {
      alert.openAlert(
        errorMessage(err, "The market report could not be removed."),
        "error",
        6000
      );
    } finally {
      setDeleting(false);
    }
  };

  const visibleReports = reports.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <LayoutWrapper pathName="admin/marketReports">
      <Container className={classes.container} maxWidth="xl">
        {initialError.length ? (
          <Alert severity="error">{initialError}</Alert>
        ) : (
          <div style={{ height: "auto", width: "100%" }}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: 16,
              }}
            >
              <Typography variant="h6">Market Reports</Typography>
              <Button color="primary" variant="outlined" onClick={openCreateForm}>
                Add Report
              </Button>
            </div>

            <Table size="medium">
              <TableHead>
                <TableRow>
                  {marketReportTableHeadCells.map((cell, index) => (
                    <TableCell
                      key={cell || `actions-${index}`}
                      style={{
                        backgroundColor: Colors.vinousRed,
                        color: "white",
                      }}
                    >
                      <Typography style={{ fontWeight: "bold" }}>
                        {cell}
                      </Typography>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {visibleReports.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={marketReportTableHeadCells.length}>
                      <Typography>
                        {loading
                          ? "Loading market reports…"
                          : "No market reports yet. Use Add Report to upload one."}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  visibleReports.map((report) => (
                    <TableRow key={report.id} hover>
                      <TableCell>{report.title}</TableCell>
                      <TableCell>
                        {monthLabel(toMonthInput(report.published_at))}
                      </TableCell>
                      <TableCell>
                        {report.is_published ? "Published" : "Hidden"}
                      </TableCell>
                      <TableCell>
                        <a
                          href={report.file_url}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          View
                        </a>
                      </TableCell>
                      <TableCell>{formatDate(report.updated_at)}</TableCell>
                      <TableCell align="right">
                        <Button
                          color="primary"
                          onClick={() => openEditForm(report)}
                        >
                          Edit
                        </Button>
                        <Button
                          color="primary"
                          onClick={() => setPendingDelete(report)}
                        >
                          Delete
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>

            <TablePagination
              component="div"
              rowsPerPageOptions={rowsPerPageOptions}
              colSpan={3}
              count={reports.length}
              rowsPerPage={rowsPerPage}
              page={page}
              SelectProps={{
                inputProps: { "aria-label": "rows per page" },
                native: true,
              }}
              onChangePage={(_, newPage) => setPage(newPage)}
              onChangeRowsPerPage={(event) => {
                setRowsPerPage(parseInt(event.target.value, 10));
                setPage(0);
              }}
            />
          </div>
        )}

        <Dialog open={isFormOpen} onClose={closeForm} fullWidth maxWidth="sm">
          <DialogTitle>
            <span style={{ fontWeight: 700 }}>
              {editing ? "Edit Market Report" : "Add Market Report"}
            </span>
          </DialogTitle>
          <DialogContent>
            {formError ? (
              <Alert severity="error" style={{ marginBottom: 16 }}>
                {formError}
              </Alert>
            ) : null}

            <TextField
              label="Report Month"
              type="month"
              variant="outlined"
              fullWidth
              value={form.publishedMonth}
              onChange={(event) => handleMonthChange(event.target.value)}
              InputLabelProps={{ shrink: true }}
              style={{ marginBottom: 16 }}
              helperText="Controls the order reports appear in for subscribers."
            />

            <TextField
              label="Title"
              variant="outlined"
              fullWidth
              value={form.title}
              onChange={(event) =>
                setForm({ ...form, title: event.target.value })
              }
              style={{ marginBottom: 8 }}
              helperText='Shown to subscribers, e.g. "May & June 2025".'
            />

            <Typography color="textSecondary" component="p">
              {editing
                ? "Drag and drop a PDF to replace the current file, or leave it as is."
                : "Drag and drop the report PDF or click to upload."}
            </Typography>
            <PdfUpload onUploaded={handleUploaded} onError={setFormError} />

            {form.fileUrl ? (
              <Typography component="p" style={{ marginBottom: 8 }}>
                <a
                  href={form.fileUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  View the PDF that will be linked
                </a>
              </Typography>
            ) : null}

            <FormControlLabel
              control={
                <Switch
                  checked={form.isPublished}
                  color="primary"
                  onChange={(event) =>
                    setForm({ ...form, isPublished: event.target.checked })
                  }
                />
              }
              label={
                form.isPublished
                  ? "Published — visible to subscribers"
                  : "Hidden — not visible to subscribers"
              }
            />
          </DialogContent>
          <DialogActions style={{ marginTop: 16 }}>
            <Button onClick={closeForm} color="primary" disabled={saving}>
              Cancel
            </Button>
            <Button onClick={handleSave} color="primary" disabled={saving}>
              {saving ? <CircularProgress size={20} /> : "Save"}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={pendingDelete !== null}
          onClose={() => setPendingDelete(null)}
          fullWidth
          maxWidth="sm"
        >
          <DialogTitle>
            <span style={{ fontWeight: 700 }}>Delete Market Report</span>
          </DialogTitle>
          <DialogContent>
            <Typography>
              {`Remove "${pendingDelete?.title}" from the market reports list?`}
            </Typography>
            <Typography color="textSecondary" style={{ marginTop: 8 }}>
              Subscribers will no longer see it. The PDF itself stays in storage,
              so anyone with a direct link can still open it.
            </Typography>
          </DialogContent>
          <DialogActions style={{ marginTop: 16 }}>
            <Button
              onClick={() => setPendingDelete(null)}
              color="primary"
              disabled={deleting}
            >
              Cancel
            </Button>
            <Button onClick={handleDelete} color="primary" disabled={deleting}>
              {deleting ? <CircularProgress size={20} /> : "Delete"}
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </LayoutWrapper>
  );
};
